import { toast } from "sonner";
import { Check, Radio, Flame, Eye, Users, MousePointerClick, Clock, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg, fmtTime, fmtDate, BASE, SITE } from "@/lib/api";
import { EmailPrefs } from "./EmailPrefs";
import { Referrals } from "./Referrals";
import { Progress } from "@/components/ui/progress";
import { Badges } from "@/components/fo/Bits";

const Step = ({ done, label, action, testid }) => (
  <div className="flex items-center gap-3 border-b border-white/5 py-3" data-testid={testid}>
    <span className={`flex h-7 w-7 items-center justify-center rounded-full border ${done ? "border-[#00F5D4] bg-[#00F5D4] text-[#07090E]" : "border-white/20 text-slate-500"}`}>{done ? <Check className="h-4 w-4" /> : null}</span>
    <span className={`flex-1 ${done ? "text-slate-400 line-through" : "text-white"}`}>{label}</span>
    {!done && action}
  </div>
);

const Onboarding = ({ d, go, reload }) => {
  const u = d.user;
  const steps = [
    ["Create your account", true],
    ["Complete profile & bio", !!(u.bio && u.genre && u.role_type), "profile"],
    ["Add a photo", !!u.photo, "profile"],
    ["Upload your first track", d.tracks.length > 0, "tracks"],
    ["Submit for review", u.status !== "draft" && u.status !== "rejected"],
  ];
  const done = steps.filter((s) => s[1]).length;
  const submit = async () => {
    try { await api.post("/me/submit"); toast.success("Submitted! We'll review your music soon."); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const left = steps.length - done;
  return (
    <div className="panel console-corners p-6 md:p-8" data-testid="onboarding-panel">
      <div className="label-mono">Go on air</div>
      <h2 className="mt-2 font-display text-2xl font-bold">{left ? `You're ${left} step${left > 1 ? "s" : ""} from going on air` : "Waiting for approval"}</h2>
      <Progress value={(done / steps.length) * 100} className="mt-4 h-2 bg-white/10" data-testid="onboarding-progress" />
      <div className="mt-4">
        {steps.map(([l, ok, tab], i) => (
          <Step key={l} done={ok} label={l} testid={`onboarding-step-${i}`}
            action={tab ? <button className="btn-ghost !px-4 !py-1.5 text-xs" onClick={() => go(tab)} data-testid={`onboarding-go-${i}`}>Do it</button>
              : <button className="btn-hot !px-4 !py-1.5 text-xs" onClick={submit} data-testid="onboarding-submit">Submit</button>} />
        ))}
      </div>
      {u.status === "rejected" && <p className="mt-4 text-sm text-[#FF2E63]">Your last submission wasn't selected. Update your profile or track and submit again.</p>}
    </div>
  );
};

const Stat = ({ icon: I, label, value, testid }) => (
  <div className="panel p-5" data-testid={testid}><I className="h-4 w-4 text-[#00F5D4]" /><div className="led mt-2 text-3xl font-bold">{value}</div><div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div></div>
);

export const Overview = ({ d, go, reload, welcome }) => {
  const u = d.user;
  const approved = u.status === "approved";
  const g = d.plan.guaranteed;
  return (
    <div className="space-y-6" data-testid="dash-overview">
      {welcome && !approved && <div className="rounded-2xl border border-[#00F5D4]/40 bg-[#00F5D4]/10 p-4 text-sm text-[#00F5D4]" data-testid="welcome-banner">Welcome to Fuorionda! Complete the steps below — approved artists among the first 50 become Founding Artists with 3 months of Pro, free.</div>}
      {!approved && <Onboarding d={d} go={go} reload={reload} />}
      {d.plan.id === "free" && (
        <div className="panel console-corners relative flex flex-wrap items-center justify-between gap-4 overflow-hidden border-[#FF2E63]/50 p-6" data-testid="go-pro-banner">
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#FF2E63]/25 blur-[60px]" />
          <div>
            <div className="label-mono !text-[#FF2E63]">Go Pro</div>
            <div className="mt-1 font-display text-2xl font-bold">5× more plays — €12.99/month</div>
            <p className="mt-1 text-sm text-slate-400">150 guaranteed plays/month (30 in prime time), unlimited tracks, full reports, home spotlight, 1 review/month.</p>
          </div>
          <button className="btn-hot" onClick={() => go("plan")} data-testid="go-pro-button">See plans</button>
        </div>
      )}
      {approved && (
        <div className="panel flex flex-wrap items-center justify-between gap-4 p-6" data-testid="approved-panel">
          <div><div className="label-mono">Your link for Instagram bio</div><div className="mt-2 break-all font-mono text-sm text-white sm:text-lg">{window.location.host}/a/{u.slug}</div><Badges badges={u.badges} plan={u.effective_plan} className="mt-3" /></div>
          <div className="grid w-full grid-cols-2 gap-3 sm:flex sm:w-auto">
            <button className="btn-ghost" data-testid="copy-link-button" onClick={() => { navigator.clipboard.writeText(`${SITE}/a/${u.slug}`); toast.success("Link copied"); }}>Copy link</button>
            <a href={`${BASE}/api/artists/${u.slug}/presskit.pdf`} className="btn-ghost" data-testid="presskit-download-button">Press kit PDF</a>
            <Link to={`/a/${u.slug}`} className="btn-hot col-span-2" data-testid="view-page-button"><ExternalLink className="h-4 w-4" />View page</Link>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat icon={Radio} label="Plays on air" value={d.totals.plays} testid="stat-plays" />
        <Stat icon={Users} label="Listeners reached" value={d.totals.listeners} testid="stat-listeners" />
        <Stat icon={Flame} label="Flames" value={d.totals.flames} testid="stat-flames" />
        <Stat icon={Eye} label="Blind Flames" value={d.totals.blind_flames} testid="stat-blind-flames" />
        <Stat icon={MousePointerClick} label="Page visits" value={d.totals.visits} testid="stat-visits" />
        <Stat icon={Flame} label="Blind Chart rank" value={d.blind_rank ? `#${d.blind_rank}` : "–"} testid="stat-blind-rank" />
        <Stat icon={Flame} label="Fan Chart rank" value={d.fan_rank ? `#${d.fan_rank}` : "–"} testid="stat-fan-rank" />
        <Stat icon={Radio} label={g ? `Plays this month / ${g} guaranteed` : "Plays this month"} value={d.plays_this_month} testid="stat-month" />
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="panel p-6" data-testid="upcoming-plays">
          {d.today_slots?.length > 0 && <>
            <div className="label-mono mb-3">Booked today (±10 min)</div>
            {d.today_slots.map((s) => <div key={s.at} className="flex items-center justify-between border-b border-white/5 py-2 text-sm" data-testid="today-slot"><span>{s.title}{s.prime && <span className="ml-2 font-mono text-[10px] text-[#FF2E63]">PRIME</span>}</span><span className="font-mono text-slate-400">{fmtTime(s.at)}{s.used ? " ✓" : ""}</span></div>)}
            <div className="mb-4" />
          </>}
          <div className="label-mono mb-4">Coming up on air</div>
          {d.upcoming.length ? d.upcoming.map((p) => <div key={p.play_id} className="flex items-center justify-between border-b border-white/5 py-2 text-sm"><span className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#FF2E63]" />{p.title}{p.blind && <span className="font-mono text-[10px] text-[#FFB800]">BLIND</span>}</span><span className="font-mono text-slate-400">{fmtTime(p.start)}</span></div>)
            : <p className="text-sm text-slate-500">Nothing scheduled in the next few tracks. We'll email you right before you go on air.</p>}
        </div>
        <div className="panel p-6" data-testid="awards-list">
          <div className="label-mono mb-4">Plan & prize history</div>
          {d.awards.length ? d.awards.map((a) => <div key={a.id} className="flex justify-between border-b border-white/5 py-2 text-sm"><span>{a.reason}</span><span className="font-mono text-xs text-slate-500">{fmtDate(a.created_at)}</span></div>)
            : <p className="text-sm text-slate-500">Win a chart to see your prizes here.</p>}
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-2"><Referrals /><EmailPrefs prefs={d.email_prefs} /></div>
    </div>
  );
};
